import {
  Badge,
  Button,
  DataTable,
  Page,
  Tag,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { config } from 'lib-shared-backend-config';
import { ampli } from 'lib-shared-tracking';

import { ApiToken, authApi } from 'apis/auth';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SettingsIntegrationsDocumentationButton } from '../../components/documentationButton/documentationButton';

import { ApiTokensTableActions } from './components/tableActions/apiTokensTableActions';
import { apiTokenTypesByVersion } from './utils/tokenType';

export const pathToApiTokensSettings = '/settings/integrations/api-tokens';

const emptyArray: ApiToken[] = [];

/** Settings page that lists all API tokens. */
export const SettingsIntegrationsApiTokensView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const { tokens, isLoading, isError, isSuccess, refetch } =
    authApi.useReadApiTokensQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        tokens: data?.data.tokens || emptyArray,
      }),
    });

  const [deleteApitoken] = authApi.useDeleteApiTokenMutation();

  const handleDeleteApiToken = async ({
    id,
    name,
  }: Pick<ApiToken, 'id' | 'name'>) => {
    ampli.settingsIntegrationsApiTokensRemove();

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${name}"`,
            description: 'Are you sure you want to delete this API token?',
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        await deleteApitoken({ tokenId: id });

        ampli.settingsIntegrationsApiTokensRemoveConfirm();

        notify(
          {
            title: 'Success',
            text: `"${name}" has been deleted.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          { title: `Error while deleting "${name}".` },
          NotificationType.Error,
        );
      }
    } else {
      ampli.settingsIntegrationsApiTokensRemoveCancel();
    }
  };

  return (
    <Page.Section
      title="All API tokens"
      titleMeta={
        <>
          {tokens.length > 0 && (
            <Badge size="small" variant="secondary">
              {tokens.length}
            </Badge>
          )}

          <SettingsIntegrationsDocumentationButton
            onClick={() => {
              ampli.settingsIntegrationsApiTokensDocumentationLinkIntegrationsApi();

              window.open(
                `https://${config.domain.integrations}/openapi`,
                '_blank',
                'noopener,noreferrer',
              );
            }}
          />
        </>
      }
      controls={
        <Button
          onClick={() =>
            navigate({
              path: `${pathToApiTokensSettings}/create`,
            })
          }
        >
          Add API token
        </Button>
      }
    >
      <DataTable<ApiToken, never>
        data={tokens}
        tableStyleProps={{
          stickyHeader: true,
        }}
        tableStatusProps={{
          loading: isLoading,
          loadingFailed: isError,
          onLoadingRetry: refetch,
          emptyState:
            !tokens.length && isSuccess
              ? {
                  title: 'Nothing to show here',
                  description: 'No API tokens created yet',
                }
              : undefined,
        }}
        columns={[
          {
            label: 'Name',
            selector: ({ name }) => name,
          },
          {
            label: 'Version',
            selector: ({ version }) => {
              if (version) {
                const tokenType = apiTokenTypesByVersion[version];

                return <Tag color={tokenType.color}>{tokenType.label}</Tag>;
              }
            },
          },
          {
            label: 'Created',
            selector: ({ created_at }) => <Timestamp time={created_at} />,
          },
          {
            label: undefined,
            style: {
              width: 0,
            },
            selector: ({ id, name, value }) => (
              <ApiTokensTableActions
                id={id}
                name={name}
                token={value}
                handleDeleteApiToken={handleDeleteApiToken}
              />
            ),
          },
        ]}
      />
    </Page.Section>
  );
};
